import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, Plus, Pencil, Trash, FileText,
  IdentificationCard, DownloadSimple,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  portalListMyBeneficiaries, portalCreateBeneficiary, portalUpdateBeneficiary,
  portalListBeneficiaryDocs, portalUploadBeneficiaryDoc, portalDeleteBeneficiaryDoc,
  getFileUrl,
} from '../../services/api';

const empty = {
  name: '', id_type: 'national_id', id_number: '',
  nin_number: '', passport_number: '', id_card_number: '',
  phone: '', email: '', address: '', date_of_birth: '',
  relationship: 'customer', notes: '',
};

const PortalEndClients = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingBen, setEditingBen] = useState(null); // null=new, doc=edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const [docsBen, setDocsBen] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await portalListMyBeneficiaries();
      setBeneficiaries(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingBen(null); setForm(empty); };
  const openEdit = (b) => {
    setEditingBen(b);
    setForm({
      name: b.name || '', id_type: b.id_type || 'national_id',
      id_number: b.id_number || '', nin_number: b.nin_number || '',
      passport_number: b.passport_number || '', id_card_number: b.id_card_number || '',
      phone: b.phone || '', email: b.email || '', address: b.address || '',
      date_of_birth: b.date_of_birth || '', relationship: b.relationship || 'customer',
      notes: b.notes || '',
    });
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingBen) {
        await portalUpdateBeneficiary(editingBen.id, form);
        toast.success('End client updated');
      } else {
        await portalCreateBeneficiary(form);
        toast.success('End client created');
      }
      setEditingBen(undefined); // close dialog
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // ----- DOCS -----
  const openDocs = async (b) => {
    setDocsBen(b);
    setDocsLoading(true);
    try {
      const r = await portalListBeneficiaryDocs(b.id);
      setDocs(r.data || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setDocsLoading(false); }
  };
  const reloadDocs = async () => {
    if (!docsBen) return;
    try { const r = await portalListBeneficiaryDocs(docsBen.id); setDocs(r.data || []); } catch { /* ignore */ }
  };
  const removeDoc = async (d) => {
    if (!window.confirm(`Remove ${d.original_filename}?`)) return;
    try {
      await portalDeleteBeneficiaryDoc(docsBen.id, d.id);
      toast.success('Document removed');
      await reloadDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await portalUploadBeneficiaryDoc(docsBen.id, fd);
      toast.success('Document uploaded');
      await reloadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isImage = (ct) => (ct || '').startsWith('image/');

  return (
    <div className="space-y-6" data-testid="portal-end-clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users weight="duotone" /> My End Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the buyers you place orders for. You can update their info and upload their ID documents at any time.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-endclient-btn">
          <Plus className="mr-2 h-4 w-4" /> Add end client
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : beneficiaries.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          You haven't added any end clients yet.
        </div>
      ) : (
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Identification</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {beneficiaries.map((b) => (
                <tr key={b.id} className="border-t border-border" data-testid={`endclient-row-${b.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{b.name}</div>
                    {b.relationship && <div className="text-xs text-muted-foreground">{b.relationship}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {b.phone && <div>{b.phone}</div>}
                    {b.email && <div className="text-muted-foreground">{b.email}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {b.nin_number && <div>NIN: {b.nin_number}</div>}
                    {b.passport_number && <div>Passport: {b.passport_number}</div>}
                    {b.id_card_number && <div>ID: {b.id_card_number}</div>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openDocs(b)} className="mr-2" data-testid={`docs-btn-${b.id}`}>
                      <FileText size={14} className="mr-1" /> Docs
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`edit-btn-${b.id}`}>
                      <Pencil size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={editingBen !== undefined} onOpenChange={(o) => !o && setEditingBen(undefined)}>
        <DialogContent className="max-w-lg" data-testid="endclient-dialog">
          <DialogHeader>
            <DialogTitle>{editingBen ? `Edit ${editingBen.name}` : 'Add end client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="ec-name" />
              </div>
              <div>
                <Label>NIN</Label>
                <Input value={form.nin_number} onChange={(e) => setForm({ ...form, nin_number: e.target.value })} data-testid="ec-nin" />
              </div>
              <div>
                <Label>Passport #</Label>
                <Input value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} data-testid="ec-passport" />
              </div>
              <div>
                <Label>ID Card #</Label>
                <Input value={form.id_card_number} onChange={(e) => setForm({ ...form, id_card_number: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ec-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Date of birth</Label>
                <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingBen(undefined)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="ec-save-btn">
                {saving ? 'Saving…' : (editingBen ? 'Save' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents dialog */}
      <Dialog open={!!docsBen} onOpenChange={(o) => !o && setDocsBen(null)}>
        <DialogContent className="max-w-2xl" data-testid="docs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IdentificationCard weight="duotone" /> Documents — {docsBen?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground text-xs">
              Upload ID scans, passport scans, address proofs, etc. Documents are saved on this end client's profile.
              You can add or remove documents anytime — even after the order is shipped.
            </p>
            <div className="flex items-center gap-3 border border-border rounded p-3 bg-muted/20">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                disabled={uploading}
                data-testid="doc-upload-input"
              />
              {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
            </div>

            {docsLoading ? (
              <p>Loading documents…</p>
            ) : docs.length === 0 ? (
              <p className="text-muted-foreground italic">No documents yet.</p>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-border rounded p-2 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      {isImage(d.content_type) ? (
                        <img src={getFileUrl(d.id)} alt={d.original_filename} className="w-10 h-10 object-cover border" />
                      ) : (
                        <FileText size={32} className="text-muted-foreground" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.original_filename}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {(d.size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <a href={getFileUrl(d.id)} target="_blank" rel="noreferrer" className="p-2 text-muted-foreground hover:text-foreground" title="Open">
                        <DownloadSimple size={16} />
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => removeDoc(d)} title="Remove">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsBen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalEndClients;
